import { useDispatch } from 'react-redux';
import React, { useEffect, useMemo, useState } from 'react';
import keyBy from 'lodash/keyBy';
import filter from 'lodash/filter';
import map from 'lodash/map';

import { MENU_VALUES } from '@API/workspace';
import useUpdateDefaultWorkspaceMutation from '@API/workspace/useUpdateDefaultWorkspaceMutation';

import useUserPermission from '@shared/api/useUserPermission';
import { MANAGEMENT_PERMISSIONS } from '@shared/constants/permissions';

import { useCurrentOrganization, useWorkspaces } from '@shared/hooks';

import { SearchInput } from '@design-system/components';
import SmallLoader from '@shared/components/SmallLoader';
import { WorkspacesList } from '@account-settings/components/WorkspacesList';
import { useDeleteWorkspaceMutation } from '@account-settings/api';
import AccountSettingsWorkspaceCard from './AccountSettingsWorkspaceCard';
import { CreateWorkspace } from '../../CreateWorkspace';
import { DeleteWorkspaceModal } from '../../DeleteWorkspaceModal';

import './Workspaces.scss';
import workspaceActions from '@/actions/workspaceActions';
import { useHistory, useLocation } from 'react-router';
import { Avatar } from '@DesignSystem/data-display';
import AdminList from './Components/AdminsList';
import useOrganizationUsers from '@/features/account-settings/api/users/useOrganizationUsers';
import useOrganizationWorkspaces from '@shared/api/useOrganizationWorkspaces';
import size from 'lodash/size';

const Workspaces = () => {
  const [searchValue, setSearchValue] = useState('');
  const { search } = useLocation();
  const dispatch = useDispatch();
  const navigate = useHistory();

  const [workspaceToRemove, setWorkspaceToRemove] = useState(null);
  const currentOrganization = useCurrentOrganization();
  const { data: memberAndPublicWorkspaces, isLoading } = useWorkspaces();
  const {
    data: orgWorkspaces = [],
    isLoading: orgWorkspacesLoading
  } = useOrganizationWorkspaces(currentOrganization?.id, {
    enabled: !!currentOrganization?.isAdmin
  });
  const { getPermissionStatus } = useUserPermission();

  const deleteWorkspaceMutation = useDeleteWorkspaceMutation();

  const { data: organizationMembers } = useOrganizationUsers(
    currentOrganization?.id
  );

  const workspaces = useMemo(() => {
    if (currentOrganization?.isAdmin) {
      const memberIds = keyBy(
        filter(memberAndPublicWorkspaces, { isMember: true }),
        'id'
      );
      const updatedOrgWorkspaces = map(orgWorkspaces, orgWorkspace => ({
        ...orgWorkspace,
        isMember: Boolean(memberIds[orgWorkspace.id])
      }));
      return updatedOrgWorkspaces;
    }
    return memberAndPublicWorkspaces;
  }, [orgWorkspaces, memberAndPublicWorkspaces, currentOrganization?.isAdmin]);
  const updateDefaultWorkspaceMutation = useUpdateDefaultWorkspaceMutation();

  const filteredWorkspaces = useMemo(
    () =>
      (workspaces || [])?.filter(
        workspace =>
          !searchValue ||
          workspace.name.toLowerCase().includes(searchValue.toLowerCase())
      ),
    [workspaces, searchValue]
  );

  const noSearchResults =
    !!workspaces.length && !filteredWorkspaces.length && !!searchValue;

  const closeDeleteModal = () => setWorkspaceToRemove(null);
  const handleDeleteConfirm = () => {
    deleteWorkspaceMutation.mutate(workspaceToRemove.id);
    setWorkspaceToRemove(null);
  };

  const handleClickMenu = workspace => option => {
    if (option.value === MENU_VALUES.deleteWorkspace) {
      return setWorkspaceToRemove(workspace);
    }

    if (option.value === MENU_VALUES.default) {
      return updateDefaultWorkspaceMutation.mutate(workspace.id);
    }

    if (option.value === MENU_VALUES.manageUsers) {
      dispatch(
        workspaceActions.setManageWorkspaceModalState({
          open: true,
          workspaceId: workspace?.id,
          workspaceName: workspace?.name
        })
      );
    }
  };

  const handleOpenInviteMembersModal = workspace => () => {
    dispatch(
      workspaceActions.setInviteMembersModalState({
        open: true,
        workspaceId: workspace?.id,
        workspaceName: workspace?.name
      })
    );
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(search);
    const initialInviteId = queryParams.get('initialInviteId');
    if (!initialInviteId) return;

    const workspaceByInviteId = workspaces?.find(
      workspace => workspace.id === initialInviteId
    );
    if (!workspaceByInviteId) return;

    handleOpenInviteMembersModal(workspaceByInviteId)();
    queryParams.delete('initialInviteId');
    navigate.replace({
      search: queryParams.toString()
    });
  }, [search, workspaces, navigate]);

  const renderCard = ({ style, index }) => {
    const workspace = filteredWorkspaces[index];

    return (
      <div style={style} key={workspace.id}>
        <AccountSettingsWorkspaceCard
          workspaceId={workspace.id}
          workspaceStorageUsage={workspace.storageUsage}
          workspaceName={workspace.name}
          isMember={workspace.isMember}
          projectsCount={workspace.projectsCount}
          isAdmin={currentOrganization?.isAdmin}
          isDefault={workspace.isDefault}
          createdBy={workspace.createdBy}
          createdAt={workspace.createdAt}
          onClickExtraMenu={handleClickMenu(workspace)}
          onClickInvite={handleOpenInviteMembersModal(workspace)}
          disabledInviteButton={
            !getPermissionStatus({
              workspaceName: workspace.name,
              permissionKey:
                MANAGEMENT_PERMISSIONS.INVITE_USERS_FROM_ORGANIZATION
            }) &&
            !getPermissionStatus({
              workspaceName: workspace.name,
              permissionKey:
                MANAGEMENT_PERMISSIONS.INVITE_USERS_OUT_OF_ORGANIZATION
            })
          }
        />
      </div>
    );
  };

  return (
    <>
      <DeleteWorkspaceModal
        open={Boolean(workspaceToRemove)}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        workspaceName={workspaceToRemove?.name || ''}
      />
      <div className="workspaces-container">
        <h4 className="page-title">Workspaces</h4>

        <div className="organization-details">
          <div className="organization-details-group">
            <Avatar
              src={currentOrganization?.logoUrl}
              square
              width="24px"
              showLetters
              letters={currentOrganization?.name[0]}
              backgroundColor="#12A4B4"
              lettersFontSize="15px"
            />
            <p>{currentOrganization?.name}</p>
          </div>

          <div className="organization-details-group">
            <p className="group-title">Members</p>
            <p>{size(organizationMembers)}</p>
          </div>

          <div className="organization-details-group">
            <p className="group-title">Admins</p>
            <AdminList />
          </div>
        </div>

        {isLoading || orgWorkspacesLoading ? (
          <SmallLoader
            primaryMessage="Loading..."
            secondaryMessage="Loading workspaces"
          />
        ) : (
          <>
            <div className="workspaces-controller-header">
              <div>
                <SearchInput
                  placeholder="Search"
                  value={searchValue}
                  onChange={e => setSearchValue(e.target.value)}
                  onClearIconClick={() => setSearchValue('')}
                />
              </div>

              <CreateWorkspace />
            </div>

            <WorkspacesList
              noSearchResults={noSearchResults}
              rowCount={filteredWorkspaces.length}
              renderCard={renderCard}
            />
          </>
        )}
      </div>
    </>
  );
};

export default Workspaces;
